import {OIDC_USER_STORAGE_KEY} from './oidcConfig';
import {derivePrimaryRole, effectiveRoles} from './roles';

// Read the stored OIDC user without needing React context.
// oidc-client-ts writes it under `oidc.user:<authority>:<client_id>`.
//
// localStorage is the live store (shared across tabs — see oidcConfig).
// sessionStorage is only read as a fallback so a tab that was already open
// when this change shipped keeps working until its next login instead of
// being bounced to the IdP mid-session.
const readStoredOidcUser = () => {
  let stored = null;
  try {
    stored = window.localStorage.getItem(OIDC_USER_STORAGE_KEY)
      || window.sessionStorage.getItem(OIDC_USER_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

export const getOidcAccessToken = () => readStoredOidcUser()?.access_token || '';

export const getStoredOidcUser = readStoredOidcUser;

export const isOidcAuthenticated = () => {
  return !!getOidcAccessToken();
};

// Decode JWT payload without verification (for reading claims client-side)
export const decodeJwtPayload = (token) => {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(payload);
  } catch {
    return null;
  }
};

// Primary role derived from the access token. Returns '' when there is no
// OIDC token (legacy cookie login) so callers can fall back.
//
// Route guards MUST prefer this over the localStorage profile role. The two
// can disagree (a stale profile from a prior login, a renew that returned a
// different account), and when they do, a guard reading the profile while the
// login page reads the token bounces the user between /admin-panel and /login
// until React throws "Maximum update depth exceeded" (error #185).
export const getOidcRole = () => {
  const token = getOidcAccessToken();
  if (!token) return '';
  const claims = decodeJwtPayload(token);
  if (!claims) return '';
  return derivePrimaryRole(effectiveRoles(claims));
};

// Get org_id and org_name from the access token JWT
export const getOidcOrgInfo = () => {
  const token = getOidcAccessToken();
  const claims = decodeJwtPayload(token);
  if (!claims) return { orgId: '', orgName: '' };
  return {
    orgId: claims.org_id || '',
    orgName: claims.org_name || '',
  };
};
